import React, { useState, useMemo } from 'react';
import { VscDash, VscListSelection } from 'react-icons/vsc';
import {
  MdOutlineLocationOn,
  MdOutlineEvent,
  MdOutlineAccessTime,
  MdOutlinePerson,
  MdOutlineEdit,
  MdLink,
} from 'react-icons/md';
import { BsPersonCircle } from 'react-icons/bs';
import { SiGooglemeet } from 'react-icons/si';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import toast from 'react-hot-toast';

import { sanitizeHtml } from '../../utils/sanitize';

// --- ReactQuill Config ---
const modules = {
  toolbar: [
    [{ font: [] }, { size: [] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['blockquote'],
    ['link', 'image'],
    ['clean'],
  ],
};

const formats = [
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'list',
  'bullet',
  'align',
  'blockquote',
  'link',
  'image',
  'clean',
];

const getAttendeeEmail = (attendee) => {
  if (
    typeof attendee === 'object' &&
    attendee !== null &&
    attendee.emailAddress
  ) {
    return attendee.emailAddress.address || attendee.emailAddress.name || '';
  }
  if (typeof attendee === 'object' && attendee !== null && attendee.email) {
    return attendee.email;
  }
  if (typeof attendee === 'string') return attendee;
  return '';
};

export default function CalendarEventEditor({
  event = {},
  onUpdate,
  approvalNeeded = false,
}) {
  const [isEditable, setIsEditable] = useState(false);
  const [originalEventOnEdit, setOriginalEventOnEdit] = useState(null);
  const [attendeeInputValue, setAttendeeInputValue] = useState('');

  const normalizedAttendees = useMemo(() => {
    if (!event || !Array.isArray(event.attendees)) return [];
    return event.attendees.map(getAttendeeEmail).filter(Boolean);
  }, [event]);

  if (!event) return null;

  const getLocationDisplayName = (location) => {
    if (
      typeof location === 'object' &&
      location !== null &&
      location.displayName
    ) {
      return location.displayName;
    }
    return location || '';
  };

  const updateEventField = (field, value) => {
    if (onUpdate) onUpdate({ ...event, [field]: value });
  };

  const handleInputChange = (e) =>
    updateEventField(e.target.name, e.target.value);
  const handleDescriptionChange = (content) =>
    updateEventField('description', content);

  const handleAttendeeKeyDown = (e) => {
    if (['Enter', ',', ' '].includes(e.key)) {
      e.preventDefault();
      const email = attendeeInputValue.trim().replace(/,$/, '');
      if (email && !normalizedAttendees.includes(email)) {
        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          updateEventField('attendees', [...(event.attendees || []), email]);
          setAttendeeInputValue('');
        } else {
          toast.error('Invalid email format!');
        }
      } else if (email) {
        setAttendeeInputValue('');
      }
    }
  };

  const removeAttendee = (indexToRemove) => {
    updateEventField(
      'attendees',
      (event.attendees || []).filter((_, index) => index !== indexToRemove),
    );
  };

  const handleEditClick = () => {
    setOriginalEventOnEdit(event);
    setIsEditable(true);
  };

  const handleCancel = () => {
    if (onUpdate) onUpdate(originalEventOnEdit);
    setIsEditable(false);
    setOriginalEventOnEdit(null);
  };

  const handleSave = () => {
    if (!event.title || !event.title.trim()) {
      toast.error('Event title is required.');
      return; // ← blocks save
    }
    setIsEditable(false);
    setOriginalEventOnEdit(null);
    toast.success('Event changes are staged for approval.');
  };

  // --- Shared input class ---
  const inputClass =
    'w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 p-1 pl-10 focus:outline-none focus:ring-2 focus:ring-[#5652CF] transition-colors';

  return (
    <div className="flex w-full flex-col items-start justify-start gap-2 pt-0 md:flex-row">
      <div className="mb-4 flex w-full flex-col gap-4 rounded-xl bg-white p-4 text-zinc-800 transition-colors dark:bg-zinc-900 dark:text-zinc-100">
        {/* ── Title Section ── */}
        <div className="text-md flex w-full flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
          <div className="relative flex w-full items-center">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3">
              <MdOutlineEvent
                className="text-zinc-400 dark:text-zinc-500"
                size={20}
              />
            </div>
            {isEditable ? (
              <input
                type="text"
                name="title"
                value={event.title || ''}
                onChange={handleInputChange}
                placeholder="Enter event title..."
                className={`text-md w-full rounded-lg border p-1 pl-10 font-bold transition-colors focus:outline-none focus:ring-2
    ${
      !event.title?.trim()
        ? 'border-red-400 placeholder-red-300 focus:ring-red-400'
        : 'border-zinc-300 focus:ring-[#5652CF] dark:border-zinc-600'
    }
    bg-white text-zinc-800 dark:bg-zinc-800 dark:text-zinc-100`}
              />
            ) : (
              <h2 className="text-md w-full break-words pl-10 font-bold text-zinc-900 dark:text-zinc-50">
                {event.title}
              </h2>
            )}
          </div>
          <div className="flex w-full items-center gap-2 sm:w-auto">
            {approvalNeeded && !isEditable && (
              <div className="flex items-center justify-center rounded-full px-2 py-2 transition-all duration-200 hover:bg-zinc-100 dark:hover:bg-zinc-800">
                <MdOutlineEdit
                  size={20}
                  className="cursor-pointer text-zinc-600 dark:text-zinc-400"
                  onClick={handleEditClick}
                />
              </div>
            )}
            <div className="rounded-md bg-[#5652CF] px-3 py-1 text-sm font-semibold text-white">
              Event
            </div>
          </div>
        </div>

        {/* ── Date & Time Section ── */}
        <div className="flex flex-wrap items-center gap-2 border-b border-zinc-200 pb-2 text-sm dark:border-zinc-700">
          {event.date && (
            <div className="flex items-center gap-2">
              <div className="relative flex items-center">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <MdOutlineEvent
                    className="text-zinc-400 dark:text-zinc-500"
                    size={20}
                  />
                </div>
                {isEditable ? (
                  <input
                    type="date"
                    name="date"
                    value={event.date || ''}
                    onChange={handleInputChange}
                    className={inputClass}
                  />
                ) : (
                  <span className="pl-10 text-zinc-700 dark:text-zinc-300">
                    {event.date}
                  </span>
                )}
              </div>
              <VscDash className="text-zinc-400 dark:text-zinc-500" />
            </div>
          )}
          {(isEditable || event.endDate) && (
            <div className="flex items-center gap-2">
              <div className="relative flex items-center">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <MdOutlineEvent
                    className="text-zinc-400 dark:text-zinc-500"
                    size={20}
                  />
                </div>
                {isEditable ? (
                  <input
                    type="date"
                    name="endDate"
                    value={event.endDate || event.date || ''}
                    onChange={handleInputChange}
                    className={inputClass}
                  />
                ) : (
                  <span className="pl-10 text-zinc-700 dark:text-zinc-300">
                    {event.endDate}
                  </span>
                )}
              </div>
              <VscDash className="text-zinc-400 dark:text-zinc-500" />
            </div>
          )}
          {event.startTime && (
            <div className="flex items-center gap-2">
              <div className="relative flex items-center">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <MdOutlineAccessTime
                    className="text-zinc-400 dark:text-zinc-500"
                    size={20}
                  />
                </div>
                {isEditable ? (
                  <input
                    type="time"
                    name="startTime"
                    value={event.startTime || ''}
                    onChange={handleInputChange}
                    className={inputClass}
                  />
                ) : (
                  <span className="pl-10 text-zinc-700 dark:text-zinc-300">
                    {event.startTime}
                  </span>
                )}
              </div>
              <VscDash className="text-zinc-400 dark:text-zinc-500" />
            </div>
          )}
          {event.endTime && (
            <div className="relative flex items-center">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                <MdOutlineAccessTime
                  className="text-zinc-400 dark:text-zinc-500"
                  size={20}
                />
              </div>
              {isEditable ? (
                <input
                  type="time"
                  name="endTime"
                  value={event.endTime || ''}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              ) : (
                <span className="pl-10 text-zinc-700 dark:text-zinc-300">
                  {event.endTime}
                </span>
              )}
            </div>
          )}
        </div>

        {/* ── Details Section ── */}
        <div className="flex w-full flex-col gap-4 pt-2 lg:flex-row">
          <div className="flex w-full flex-col gap-4 text-sm">
            {/* Location */}
            {(isEditable ||
              (event.location && getLocationDisplayName(event.location))) && (
              <div className="relative flex w-full items-center">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <MdOutlineLocationOn
                    className="text-zinc-400 dark:text-zinc-500"
                    size={20}
                  />
                </div>
                {isEditable ? (
                  <input
                    type="text"
                    name="location"
                    value={getLocationDisplayName(event.location)}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Add a location..."
                  />
                ) : (
                  <span className="break-words pl-10 text-zinc-700 dark:text-zinc-300">
                    {getLocationDisplayName(event.location)}
                  </span>
                )}
              </div>
            )}

            {/* Meet Link */}
            {isEditable ? (
              <div className="relative flex w-full items-center">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <MdLink
                    className="text-zinc-400 dark:text-zinc-500"
                    size={20}
                  />
                </div>
                <input
                  type="text"
                  name="meetLink"
                  value={event.meetLink || ''}
                  onChange={handleInputChange}
                  className={inputClass}
                  placeholder="Add a meeting link..."
                />
              </div>
            ) : event.meetLink ? (
              <div className="relative flex w-full items-center">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <MdLink
                    className="text-zinc-400 dark:text-zinc-500"
                    size={20}
                  />
                </div>
                <a
                  href={event.meetLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="break-words pl-10 text-[#5652CF] hover:underline dark:text-indigo-400"
                >
                  {event.meetLink}
                </a>
              </div>
            ) : (
              <div className="flex items-center gap-3 pl-3">
                <SiGooglemeet className="h-5 w-5 text-zinc-400 dark:text-zinc-500" />
                <div className="flex flex-col">
                  <div className="w-fit rounded-lg bg-[#5652CF] px-4 py-1 font-semibold text-white">
                    Awaiting Approval
                  </div>
                  <span className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                    Meeting link will be generated
                  </span>
                </div>
              </div>
            )}

            {/* Organizer */}
            {(isEditable || event.organizer) && (
              <div className="relative flex w-full items-center">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <MdOutlinePerson
                    className="text-zinc-400 dark:text-zinc-500"
                    size={20}
                  />
                </div>
                {isEditable ? (
                  <input
                    type="text"
                    name="organizer"
                    value={event.organizer || ''}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Organizer..."
                  />
                ) : (
                  <div className="flex items-center gap-2 pl-10">
                    <span className="text-zinc-600 dark:text-zinc-400">
                      Created by
                    </span>
                    <div className="flex items-center gap-2 rounded-xl bg-zinc-100 px-2 py-1 dark:bg-zinc-700">
                      <BsPersonCircle className="h-4 w-4 text-zinc-500 dark:text-zinc-400" />
                      <span className="text-xs font-medium text-zinc-700 dark:text-zinc-200">
                        {event.organizer}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Description Section ── */}
        {(isEditable || event.description) && (
          <div className="flex items-start gap-4">
            <div className="w-6 px-3">
              <VscListSelection
                size={20}
                className="text-zinc-400 dark:text-zinc-500"
              />
            </div>
            {isEditable ? (
              <ReactQuill
                value={event.description || ''}
                onChange={handleDescriptionChange}
                modules={modules}
                formats={formats}
                className="w-full text-xs text-zinc-700 dark:text-zinc-200"
              />
            ) : (
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(event.description),
                }}
                className="quill-read-only w-full break-words text-sm text-zinc-700 dark:text-zinc-300"
              />
            )}
          </div>
        )}

        {/* ── Attendees Section ── */}
        {(normalizedAttendees.length > 0 || isEditable) && (
          <div className="flex flex-col gap-2">
            <div className="font-semibold text-zinc-800 dark:text-zinc-100">
              Attendees
            </div>
            {isEditable ? (
              <div className="flex w-full flex-wrap items-center gap-1 rounded-lg border border-zinc-300 bg-white p-1 text-xs dark:border-zinc-600 dark:bg-zinc-800">
                {normalizedAttendees.map((email, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center rounded-lg bg-zinc-100 px-2 py-1 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200"
                  >
                    {email}
                    <button
                      onClick={() => removeAttendee(index)}
                      className="ml-2 text-zinc-500 hover:text-red-400 dark:text-zinc-400 dark:hover:text-red-400"
                    >
                      &times;
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  className="ml-1 min-w-[100px] flex-grow bg-transparent text-zinc-700 placeholder-zinc-400 outline-none dark:text-zinc-200 dark:placeholder-zinc-500"
                  placeholder="Add attendee email..."
                  value={attendeeInputValue}
                  onChange={(e) => setAttendeeInputValue(e.target.value)}
                  onKeyDown={handleAttendeeKeyDown}
                />
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {normalizedAttendees.map((email, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-100 px-2 py-1 shadow-sm dark:border-zinc-700 dark:bg-zinc-800"
                  >
                    <BsPersonCircle className="h-4 w-4 text-zinc-500 dark:text-zinc-400" />
                    <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
                      {email}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Action Buttons ── */}
        <div className="mt-2 flex w-full flex-col items-end justify-end gap-2 sm:flex-row sm:items-center">
          {isEditable && (
            <>
              <button
                onClick={handleCancel}
                className="w-full rounded-md bg-zinc-500 px-3 py-1 text-sm text-white transition-colors hover:bg-zinc-600 dark:bg-zinc-600 dark:hover:bg-zinc-500 sm:w-fit"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="w-full rounded-md bg-[#5652CF] px-3 py-1 text-sm text-white transition-colors hover:bg-[#4845b0] sm:w-fit"
              >
                Save
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
